<template>
  <div ref="rootEl" class="ragChat-messages" @click="$emit('citationClick', $event)">
    <!-- 欢迎区：空会话且未加载时显示（由父级注入 ChatWelcome） -->
    <slot v-if="messages.length === 0 && !loading" name="welcome" />

    <!-- 消息列表 -->
    <ChatMessageItem
      v-for="(msg, index) in messages"
      :key="msg._id || index"
      :msg="msg"
      :loading="loading"
      :thinking-enabled="thinkingEnabled"
      :hide-clarify="!!hideClarifyId && msg._id === hideClarifyId"
      @toggle-reasoning="(m) => $emit('toggleReasoning', m)"
      @toggle-tool-node="(m, i) => $emit('toggleToolNode', m, i)"
      @toggle-tool-group="(m, r) => $emit('toggleToolGroup', m, r)"
      @toggle-web-search="(m) => $emit('toggleWebSearch', m)"
      @toggle-stage-panel="(m) => $emit('toggleStagePanel', m)"
      @toggle-turn-card="(m) => $emit('toggleTurnCard', m)"
      @clarify="(m, o) => $emit('clarify', m, o)"
      @view-source="(s) => $emit('viewSource', s)"
      @view-trace="(m) => $emit('viewTrace', m)"
      @speak="(m) => $emit('speak', m)"
      @regenerate="(m) => $emit('regenerate', m)"
      @resume="(m) => $emit('resume', m)"
    />

    <!-- Loading placeholder when waiting for first token -->
    <div
      v-if="loading && (messages.length === 0 || messages[messages.length - 1].role !== 'assistant')"
      class="message-item assistant"
    >
      <div class="message-header">
        <el-tag type="success" size="small" effect="plain">AI</el-tag>
      </div>
      <div class="message-content">
        <div class="typing-indicator">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import ChatMessageItem from './ChatMessageItem.vue'

const props = defineProps({
  messages: { type: Array, required: true },
  loading: { type: Boolean, default: false },
  thinkingEnabled: { type: Boolean, default: false },
  // 被底部 V2 澄清浮窗接管的消息 _id：该消息隐藏内嵌澄清卡片（浮窗统一交互）
  hideClarifyId: { type: [String, Number], default: null },
  // 滚动容器回写：接收函数 (el)=>void，组件挂载后把根容器 DOM 交还父级供 useChatStream.scrollToBottom 使用
  messagesRef: { type: [Object, Function], default: null }
})

defineEmits([
  'citationClick',
  'toggleReasoning',
  'toggleToolNode',
  'toggleToolGroup',
  'toggleWebSearch',
  'toggleStagePanel',
  'toggleTurnCard',
  'clarify',
  'viewSource',
  'viewTrace',
  'speak',
  'regenerate',
  'resume'
])

const rootEl = ref(null)
onMounted(() => {
  if (typeof props.messagesRef === 'function') {
    props.messagesRef(rootEl.value)
  } else if (props.messagesRef) {
    props.messagesRef.value = rootEl.value
  }
})
</script>

<style scoped>
.ragChat-messages {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 24px 20px;
  background-color: #fafafa;
  display: flex;
  flex-direction: column;
}

/* 窄屏适配：收窄消息区内边距 */
@media (max-width: 768px) {
  .ragChat-messages {
    padding: 16px;
  }
}

/* Loading 占位所需的消息结构样式 */
.message-item {
  padding: 12px 16px;
}

.message-item:hover {
  background-color: #fafbfc;
}

.message-item.assistant {
  background-color: #fafbfc;
}

.message-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.message-content {
  font-size: 14px;
  line-height: 1.7;
  color: #1f2328;
}

.typing-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  background-color: #8b949e;
  border-radius: 50%;
  animation: typing-bounce 1.4s infinite ease-in-out;
}

.typing-indicator span:nth-child(1) {
  animation-delay: 0s;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing-bounce {
  0%, 80%, 100% {
    transform: scale(0.6);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
